import React, { useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Divider,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  InputAdornment,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Inventory as InventoryIcon,
  AttachMoney as AttachMoneyIcon,
  LocalOffer as LocalOfferIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { createShopifyProduct } from '../services/shopifyAPIService';

const AddShopifyProduct = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Basic Product Info
  const [productData, setProductData] = useState({
    title: '',
    bodyHtml: '',
    vendor: '',
    productType: '',
    status: 'draft',
    tags: '',
    handle: '',
    seoTitle: '',
    seoDescription: '',
  });

  // Variants
  const [variants, setVariants] = useState([
    {
      title: 'Default Title',
      price: 0,
      compareAtPrice: null,
      sku: '',
      barcode: '',
      inventoryQuantity: 0,
      inventoryPolicy: 'deny',
      inventoryManagement: 'shopify',
      fulfillmentService: 'manual',
      weight: null,
      weightUnit: 'kg',
      requiresShipping: true,
      taxable: true,
      option1: null,
      option2: null,
      option3: null,
      position: 1,
    },
  ]);

  // Product Options
  const [options, setOptions] = useState([
    {
      name: 'Title',
      values: ['Default Title'],
      position: 1,
    },
  ]);

  // Images - Changed to handle file objects and base64
  const [images, setImages] = useState([]);

  const [errors, setErrors] = useState({});

  const steps = ['Basic Info', 'Variants & Pricing', 'Images & SEO', 'Review & Submit'];

  // Handle product data change
  const handleProductChange = (field, value) => {
    setProductData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Auto-generate handle from title
    if (field === 'title') {
      const handle = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setProductData(prev => ({
        ...prev,
        handle: handle,
      }));
    }
  };

  // Handle variant change
  const handleVariantChange = (index, field, value) => {
    const updatedVariants = [...variants];
    updatedVariants[index] = {
      ...updatedVariants[index],
      [field]: value,
    };
    setVariants(updatedVariants);
  };

  // Add new variant
  const handleAddVariant = () => {
    setVariants([
      ...variants,
      {
        title: `Variant ${variants.length + 1}`,
        price: 0,
        compareAtPrice: null,
        sku: '',
        barcode: '',
        inventoryQuantity: 0,
        inventoryPolicy: 'deny',
        inventoryManagement: 'shopify',
        fulfillmentService: 'manual',
        weight: null,
        weightUnit: 'kg',
        requiresShipping: true,
        taxable: true,
        option1: null,
        option2: null,
        option3: null,
        position: variants.length + 1,
      },
    ]);
  };

  // Remove variant
  const handleRemoveVariant = (index) => {
    if (variants.length > 1) {
      setVariants(variants.filter((_, i) => i !== index));
    } else {
      toast.warning('At least one variant is required');
    }
  };

  // Handle option change
  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: value,
    };
    setOptions(updatedOptions);
  };

  // Add new option
  const handleAddOption = () => {
    if (options.length < 3) {
      setOptions([
        ...options,
        {
          name: '',
          values: [''],
          position: options.length + 1,
        },
      ]);
    } else {
      toast.warning('Maximum 3 options allowed');
    }
  };

  // Remove option
  const handleRemoveOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  // Add option value
  const handleAddOptionValue = (optionIndex) => {
    const updatedOptions = [...options];
    updatedOptions[optionIndex].values.push('');
    setOptions(updatedOptions);
  };

  // Update option value
  const handleUpdateOptionValue = (optionIndex, valueIndex, value) => {
    const updatedOptions = [...options];
    updatedOptions[optionIndex].values[valueIndex] = value;
    setOptions(updatedOptions);
  };

  // Remove option value
  const handleRemoveOptionValue = (optionIndex, valueIndex) => {
    const updatedOptions = [...options];
    if (updatedOptions[optionIndex].values.length > 1) {
      updatedOptions[optionIndex].values.splice(valueIndex, 1);
      setOptions(updatedOptions);
    }
  };

  // ✅ Convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // ✅ Handle image file selection
  const handleImageFileChange = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      toast.error('Please upload only image files (JPEG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file sizes (max 5MB per image)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      toast.error('Some images are too large. Maximum size is 5MB per image.');
      return;
    }

    try {
      setLoading(true);
      
      // Convert all files to base64
      const imagePromises = files.map(async (file) => {
        const base64 = await convertFileToBase64(file);
        const preview = URL.createObjectURL(file);
        
        return {
          file,
          base64,
          preview,
          name: file.name,
          size: file.size,
        };
      });

      const newImages = await Promise.all(imagePromises);
      
      setImages(prev => [...prev, ...newImages]);
      toast.success(`${newImages.length} image(s) uploaded successfully`);
      
    } catch (error) {
      console.error('Error converting images:', error);
      toast.error('Failed to upload images');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Remove image
  const handleRemoveImage = (index) => {
    setImages(prev => {
      const newImages = [...prev];
      // Revoke the object URL to free up memory
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
    toast.info('Image removed');
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!productData.title.trim()) {
      newErrors.title = 'Product title is required';
    }

    if (variants.some(v => v.price <= 0)) {
      newErrors.price = 'All variants must have a valid price';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    try {
      setLoading(true);

      // Clean up variants - remove empty option fields
      const cleanedVariants = variants.map(v => ({
        ...v,
        option1: v.option1 || null,
        option2: v.option2 || null,
        option3: v.option3 || null,
      }));

      // Clean up options - filter out empty values
      const cleanedOptions = options
        .filter(opt => opt.name.trim() && opt.values.some(v => v.trim()))
        .map(opt => ({
          ...opt,
          values: opt.values.filter(v => v.trim()),
        }));

      // ✅ Extract base64 strings from images
      const imageFiles = images.map(img => img.base64);

      const payload = {
        ...productData,
        variants: cleanedVariants,
        options: cleanedOptions,
        imageFiles, // ✅ Send as base64 strings
      };

      console.log('📦 Creating Shopify product:', {
        ...payload,
        imageFiles: `${imageFiles.length} images (base64)`,
      });

      const response = await createShopifyProduct(payload);

      console.log('✅ Product created:', response);

      toast.success('Product created successfully in Shopify!');
      navigate('/shopify/products');
    } catch (error) {
      console.error('❌ Error creating product:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create product';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle step navigation
  const handleNext = () => {
    if (activeStep === 0 && !productData.title.trim()) {
      toast.warning('Product title is required');
      return;
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderBasicInfo();
      case 1:
        return renderVariantsAndPricing();
      case 2:
        return renderImagesAndSEO();
      case 3:
        return renderReview();
      default:
        return null;
    }
  };

  // Step 1: Basic Info
  const renderBasicInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label="Product Title"
          value={productData.title}
          onChange={(e) => handleProductChange('title', e.target.value)}
          error={!!errors.title}
          helperText={errors.title}
          placeholder="e.g., Classic T-Shirt"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Description (HTML)"
          value={productData.bodyHtml}
          onChange={(e) => handleProductChange('bodyHtml', e.target.value)}
          placeholder="Enter product description (HTML supported)"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Vendor"
          value={productData.vendor}
          onChange={(e) => handleProductChange('vendor', e.target.value)}
          placeholder="e.g., Nike, Adidas"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Product Type"
          value={productData.productType}
          onChange={(e) => handleProductChange('productType', e.target.value)}
          placeholder="e.g., Clothing, Electronics"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={productData.status}
            label="Status"
            onChange={(e) => handleProductChange('status', e.target.value)}
          >
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Tags"
          value={productData.tags}
          onChange={(e) => handleProductChange('tags', e.target.value)}
          placeholder="e.g., summer, sale, new"
          helperText="Comma-separated tags"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="URL Handle"
          value={productData.handle}
          onChange={(e) => handleProductChange('handle', e.target.value)}
          placeholder="Auto-generated from title"
          helperText="This will be used in the product URL"
        />
      </Grid>
    </Grid>
  );

  // Step 2: Variants and Pricing
  const renderVariantsAndPricing = () => (
    <Box>
      {/* Product Options */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Product Options</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddOption}
            disabled={options.length >= 3}
            size="small"
          >
            Add Option
          </Button>
        </Box>

        {options.map((option, optIndex) => (
          <Card key={optIndex} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Option Name"
                  value={option.name}
                  onChange={(e) => handleOptionChange(optIndex, 'name', e.target.value)}
                  placeholder="e.g., Size, Color"
                  sx={{ flex: 1, mr: 2 }}
                />
                <IconButton
                  color="error"
                  onClick={() => handleRemoveOption(optIndex)}
                  disabled={options.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Option Values
              </Typography>

              {option.values.map((value, valIndex) => (
                <Box key={valIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={value}
                    onChange={(e) => handleUpdateOptionValue(optIndex, valIndex, e.target.value)}
                    placeholder="e.g., Small, Red"
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveOptionValue(optIndex, valIndex)}
                    disabled={option.values.length === 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddOptionValue(optIndex)}
              >
                Add Value
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Variants */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Variants</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddVariant}
            size="small"
          >
            Add Variant
          </Button>
        </Box>

        {variants.map((variant, index) => (
          <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Variant {index + 1}
                </Typography>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveVariant(index)}
                  disabled={variants.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Variant Title"
                    value={variant.title}
                    onChange={(e) => handleVariantChange(index, 'title', e.target.value)}
                    placeholder="e.g., Small / Red"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Price"
                    value={variant.price}
                    onChange={(e) => handleVariantChange(index, 'price', parseFloat(e.target.value))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    error={errors.price && variant.price <= 0}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Compare At Price"
                    value={variant.compareAtPrice || ''}
                    onChange={(e) => handleVariantChange(index, 'compareAtPrice', e.target.value ? parseFloat(e.target.value) : null)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="SKU"
                    value={variant.sku}
                    onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                    placeholder="Stock Keeping Unit"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Barcode"
                    value={variant.barcode}
                    onChange={(e) => handleVariantChange(index, 'barcode', e.target.value)}
                    placeholder="ISBN, UPC, GTIN, etc."
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Inventory Quantity"
                    value={variant.inventoryQuantity}
                    onChange={(e) => handleVariantChange(index, 'inventoryQuantity', parseInt(e.target.value))}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Weight"
                    value={variant.weight || ''}
                    onChange={(e) => handleVariantChange(index, 'weight', e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Weight Unit</InputLabel>
                    <Select
                      value={variant.weightUnit}
                      label="Weight Unit"
                      onChange={(e) => handleVariantChange(index, 'weightUnit', e.target.value)}
                    >
                      <MenuItem value="kg">kg</MenuItem>
                      <MenuItem value="g">g</MenuItem>
                      <MenuItem value="lb">lb</MenuItem>
                      <MenuItem value="oz">oz</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={variant.requiresShipping}
                        onChange={(e) => handleVariantChange(index, 'requiresShipping', e.target.checked)}
                      />
                    }
                    label="Requires Shipping"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={variant.taxable}
                        onChange={(e) => handleVariantChange(index, 'taxable', e.target.checked)}
                      />
                    }
                    label="Taxable"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );

  // Step 3: Images and SEO
  const renderImagesAndSEO = () => (
    <Grid container spacing={3}>
      {/* Images */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Product Images</Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            size="small"
          >
            Upload Images
            <input
              type="file"
              hidden
              multiple
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleImageFileChange}
            />
          </Button>
        </Box>

        {images.length > 0 ? (
          <Grid container spacing={2}>
            {images.map((image, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Card sx={{ position: 'relative' }}>
                  <CardContent sx={{ p: 1 }}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={image.preview}
                        variant="rounded"
                        sx={{ width: '100%', height: 150 }}
                      >
                        <ImageIcon />
                      </Avatar>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveImage(index)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'error.light' },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ mt: 1, display: 'block' }}>
                      {image.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(image.size / 1024).toFixed(2)} KB
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info" icon={<ImageIcon />}>
            No images uploaded yet. Click "Upload Images" to add product photos.
            <br />
            <Typography variant="caption" color="text.secondary">
              Supported formats: JPEG, PNG, GIF, WEBP (Max 5MB per image)
            </Typography>
          </Alert>
        )}
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>

      {/* SEO */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Search Engine Optimization
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="SEO Title"
          value={productData.seoTitle}
          onChange={(e) => handleProductChange('seoTitle', e.target.value)}
          placeholder="Product title for search engines"
          helperText={`${productData.seoTitle.length} / 70 characters`}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="SEO Description"
          value={productData.seoDescription}
          onChange={(e) => handleProductChange('seoDescription', e.target.value)}
          placeholder="Product description for search engines"
          helperText={`${productData.seoDescription.length} / 160 characters`}
        />
      </Grid>
    </Grid>
  );

  // Step 4: Review
  const renderReview = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Review your product details before creating it in Shopify.
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
          <List>
            <ListItem>
              <ListItemText primary="Title" secondary={productData.title} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Vendor" secondary={productData.vendor || 'N/A'} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Product Type" secondary={productData.productType || 'N/A'} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Status" secondary={<Chip label={productData.status} size="small" color="primary" />} />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Variants ({variants.length})</Typography>
          <List>
            {variants.map((variant, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={variant.title}
                  secondary={`Price: $${variant.price} | Stock: ${variant.inventoryQuantity}`}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Images ({images.length})</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {images.map((image, index) => (
              <Avatar
                key={index}
                src={image.preview}
                variant="rounded"
                sx={{ width: 80, height: 80 }}
              >
                <ImageIcon />
              </Avatar>
            ))}
            {images.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No images uploaded
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/shopify/products')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Add Shopify Product
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create a new product in your Shopify store
          </Typography>
        </Box>
      </Box>

      {/* Stepper */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        {renderStepContent()}

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? 'Creating...' : 'Create Product'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default AddShopifyProduct;